import {
    Box,
    Card,
    Table,
    TableBody,
    TableContainer,
    Typography,
    useTheme
} from '@mui/material';
import { SMRegular, TextSMRegular, SMBold } from '@/typography/Typography';
import { t } from 'i18next';
import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    TableHeader,
    TableRow,
    TableHeaderCellFirstCell,
    TableHeaderCell,
    TableCellFirstCell,
    TableCell
} from '@/components/shared/styles/Table';
import { styled } from '@mui/material/styles';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { ImportIcon } from '@/icons/ImportIcon';
import { PrimaryButtonLG } from '@/components/shared/Button';

export interface TaxCardUploadError {
    rowNumber: number;
    employeeNumber: string;
    errorMessage: string;
}

export interface TaxCardUploadWarning {
    rowNumber: number;
    employeeNumber: string;
    warningMessage: string;
}

export interface TaxCardUploadResponse {
    totalRows: number;
    successfulImports: number;
    failedImports: number;
    fileName: string;
    companyName: string;
    errors: TaxCardUploadError[];
    warnings: TaxCardUploadWarning[];
}

export interface TaxCardUploadResultDialogProps {
    open: boolean;
    uploadResult: TaxCardUploadResponse | null;
    onClose: () => void;
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: '400px',
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px'
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.background.default
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[400],
        borderRadius: '4px'
    }
}));

const SummaryContainer = styled(Box)({
    marginTop: '24px'
});

const SummaryDetailsBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
});

const ErrorsWarningsContainer = styled(Box)({
    marginTop: '24px'
});

const ExpandableCard = styled(Card)({});

const ExpandableTableRow = styled(TableRow)({
    cursor: 'pointer'
});

const ExpandedContentCell = styled(TableCell)({
    padding: 0,
    margin: 0
});

const ExpandedContentBox = styled(Box)({
    padding: '0px'
});

const ButtonContainer = styled(Box)({
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    gap: '12px'
});

export const TaxCardUploadResultDialog = ({
    open,
    uploadResult,
    onClose
}: TaxCardUploadResultDialogProps) => {
    const theme = useTheme();
    const [expandedSection, setExpandedSection] = useState<
        'errors' | 'warnings' | null
    >(null);

    return (
        <StyledDialog
            slotProps={{
                paper: {
                    style: {
                        maxWidth: 'fit-content'
                    }
                }
            }}
            open={open}
        >
            <StyledDialogContainer
                style={{
                    width: '664px',
                    padding: '24px'
                }}
            >
                <ConnectHeader
                    logo={<ImportIcon />}
                    title={t('upload_summary')}
                />

                {uploadResult && (
                    <>
                        <SummaryContainer>
                            <SummaryDetailsBox>
                                <SMRegular>
                                    <strong>{t('file_name')}:</strong>{' '}
                                    {uploadResult.fileName}
                                </SMRegular>
                                <SMRegular>
                                    <strong>
                                        {t('company_accounts.company')}:
                                    </strong>{' '}
                                    {uploadResult.companyName}
                                </SMRegular>
                                <SMRegular>
                                    <strong>{t('total_rows')}:</strong>{' '}
                                    {uploadResult.totalRows}
                                </SMRegular>
                                <SMRegular color={theme.palette.success.main}>
                                    <strong>{t('successful_imports')}:</strong>{' '}
                                    {uploadResult.successfulImports}
                                </SMRegular>
                                <SMRegular color={theme.palette.error.main}>
                                    <strong>{t('failed_imports')}:</strong>{' '}
                                    {uploadResult.failedImports}
                                </SMRegular>
                            </SummaryDetailsBox>
                        </SummaryContainer>

                        {(uploadResult.errors.length > 0 ||
                            uploadResult.warnings.length > 0) && (
                            <ErrorsWarningsContainer>
                                <ExpandableCard>
                                    <TableContainer>
                                        <Table>
                                            <TableBody>
                                                {uploadResult.errors.length >
                                                    0 && (
                                                    <Fragment>
                                                        <ExpandableTableRow
                                                            onClick={() =>
                                                                setExpandedSection(
                                                                    (prev) =>
                                                                        prev ===
                                                                        'errors'
                                                                            ? null
                                                                            : 'errors'
                                                                )
                                                            }
                                                        >
                                                            <TableCell>
                                                                <TextSMRegular
                                                                    color={
                                                                        uploadResult
                                                                            .errors
                                                                            .length
                                                                            ? 'error'
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {t(
                                                                        'number_error_found',
                                                                        {
                                                                            count: uploadResult
                                                                                .errors
                                                                                .length
                                                                        }
                                                                    )}
                                                                </TextSMRegular>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {expandedSection ===
                                                                'errors' ? (
                                                                    <KeyboardArrowUpIcon />
                                                                ) : (
                                                                    <KeyboardArrowDownIcon />
                                                                )}
                                                            </TableCell>
                                                        </ExpandableTableRow>
                                                        {expandedSection ===
                                                            'errors' && (
                                                            <TableRow>
                                                                <ExpandedContentCell
                                                                    colSpan={2}
                                                                >
                                                                    <ExpandedContentBox>
                                                                        <StyledTableContainer>
                                                                            <Table>
                                                                                <TableHeader>
                                                                                    <TableRow>
                                                                                        <TableHeaderCellFirstCell>
                                                                                            {t(
                                                                                                'row_number'
                                                                                            )}
                                                                                        </TableHeaderCellFirstCell>
                                                                                        <TableHeaderCell>
                                                                                            {t(
                                                                                                'employee_number'
                                                                                            )}
                                                                                        </TableHeaderCell>
                                                                                        <TableHeaderCell>
                                                                                            {t(
                                                                                                'error_message'
                                                                                            )}
                                                                                        </TableHeaderCell>
                                                                                    </TableRow>
                                                                                </TableHeader>
                                                                                <TableBody>
                                                                                    {uploadResult.errors.map(
                                                                                        (
                                                                                            error,
                                                                                            index
                                                                                        ) => (
                                                                                            <TableRow
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                            >
                                                                                                <TableCellFirstCell>
                                                                                                    {
                                                                                                        error.rowNumber
                                                                                                    }
                                                                                                </TableCellFirstCell>
                                                                                                <TableCell>
                                                                                                    {
                                                                                                        error.employeeNumber
                                                                                                    }
                                                                                                </TableCell>
                                                                                                <TableCell>
                                                                                                    <Typography
                                                                                                        fontSize={
                                                                                                            '12px'
                                                                                                        }
                                                                                                        color={
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .error
                                                                                                                .main
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            error.errorMessage
                                                                                                        }
                                                                                                    </Typography>
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        )
                                                                                    )}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </StyledTableContainer>
                                                                    </ExpandedContentBox>
                                                                </ExpandedContentCell>
                                                            </TableRow>
                                                        )}
                                                    </Fragment>
                                                )}

                                                {uploadResult.warnings.length >
                                                    0 && (
                                                    <Fragment>
                                                        <ExpandableTableRow
                                                            onClick={() =>
                                                                setExpandedSection(
                                                                    (prev) =>
                                                                        prev ===
                                                                        'warnings'
                                                                            ? null
                                                                            : 'warnings'
                                                                )
                                                            }
                                                        >
                                                            <TableCell>
                                                                <TextSMRegular
                                                                    color={
                                                                        uploadResult
                                                                            .warnings
                                                                            .length
                                                                            ? 'warning'
                                                                            : undefined
                                                                    }
                                                                >
                                                                    {t(
                                                                        'number_warning_found',
                                                                        {
                                                                            count: uploadResult
                                                                                .warnings
                                                                                .length
                                                                        }
                                                                    )}
                                                                </TextSMRegular>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {expandedSection ===
                                                                'warnings' ? (
                                                                    <KeyboardArrowUpIcon />
                                                                ) : (
                                                                    <KeyboardArrowDownIcon />
                                                                )}
                                                            </TableCell>
                                                        </ExpandableTableRow>
                                                        {expandedSection ===
                                                            'warnings' && (
                                                            <TableRow>
                                                                <ExpandedContentCell
                                                                    colSpan={2}
                                                                >
                                                                    <ExpandedContentBox>
                                                                        <StyledTableContainer>
                                                                            <Table>
                                                                                <TableHeader>
                                                                                    <TableRow>
                                                                                        <TableHeaderCellFirstCell>
                                                                                            {t(
                                                                                                'row_number'
                                                                                            )}
                                                                                        </TableHeaderCellFirstCell>
                                                                                        <TableHeaderCell>
                                                                                            {t(
                                                                                                'employee_number'
                                                                                            )}
                                                                                        </TableHeaderCell>
                                                                                        <TableHeaderCell>
                                                                                            {t(
                                                                                                'warning_message'
                                                                                            )}
                                                                                        </TableHeaderCell>
                                                                                    </TableRow>
                                                                                </TableHeader>
                                                                                <TableBody>
                                                                                    {uploadResult.warnings.map(
                                                                                        (
                                                                                            warning,
                                                                                            index
                                                                                        ) => (
                                                                                            <TableRow
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                            >
                                                                                                <TableCellFirstCell>
                                                                                                    {
                                                                                                        warning.rowNumber
                                                                                                    }
                                                                                                </TableCellFirstCell>
                                                                                                <TableCell>
                                                                                                    {
                                                                                                        warning.employeeNumber
                                                                                                    }
                                                                                                </TableCell>
                                                                                                <TableCell>
                                                                                                    <Typography
                                                                                                        fontSize={
                                                                                                            '12px'
                                                                                                        }
                                                                                                        color={
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .warning
                                                                                                                .main
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            warning.warningMessage
                                                                                                        }
                                                                                                    </Typography>
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        )
                                                                                    )}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </StyledTableContainer>
                                                                    </ExpandedContentBox>
                                                                </ExpandedContentCell>
                                                            </TableRow>
                                                        )}
                                                    </Fragment>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </ExpandableCard>
                            </ErrorsWarningsContainer>
                        )}

                        <ButtonContainer>
                            <PrimaryButtonLG
                                onClick={onClose}
                                sx={{ width: '100%', height: '40px' }}
                            >
                                <SMBold textTransform={'none'}>
                                    {t('close')}
                                </SMBold>
                            </PrimaryButtonLG>
                        </ButtonContainer>
                    </>
                )}
            </StyledDialogContainer>
        </StyledDialog>
    );
};
